import React, { useState, useEffect } from "react";
import MaskedInput from "react-text-mask";
import createNumberMask from "text-mask-addons/dist/createNumberMask";

import { useRentalInvoiceEditContext } from "./RentalInvoiceEditContainer";
import { formatCurrency } from "./currency";

const inputWrapperStyle = {
  border: "1px solid var(--sl-border-secondary, #ececec)",
  borderRadius: "8px",
  width: "100%",
};

const RentalInvoiceTaxFeeRow = ({ taxFee }) => {
  const {
    rentalInvoice,
    updateNestedAttributes,
    deleteNested,
  } = useRentalInvoiceEditContext();

  const [name, setName] = useState(taxFee.name || "");
  const [rate, setRate] = useState(taxFee.rate || 0);

  const categoryOptions = [...new Set(
    (rentalInvoice.rental_invoice_items || [])
      .map((item) => item.category)
      .filter((category) => category && category.length > 0)
  )];

  const currentCategory = taxFee.category || "";
  const isOrphanedCategory = currentCategory.length > 0 && !categoryOptions.includes(currentCategory);

  const updateTaxFeeState = (attributes) => {
    updateNestedAttributes("rental_invoice_tax_fees_attributes", { id: taxFee.id, ...attributes });
  };

  useEffect(() => {
    // Name is required; never push a blank into the debounced whole-form save,
    // where its 422 would block unrelated edits until fixed.
    if (name.trim().length > 0 && (taxFee.name || "") !== name) updateTaxFeeState({ name });
  }, [name]);

  useEffect(() => {
    if ((taxFee.rate || 0) !== rate) updateTaxFeeState({ rate });
  }, [rate]);

  const deleteTaxFee = () => {
    deleteNested("rental_invoice_tax_fees_attributes", taxFee.id);
  };

  return (
    <div className="d-flex flex-wrap align-items-end" style={{ gap: "20px" }}>
      <div className="flex-grow-1" style={{ minWidth: "200px" }}>
        <div className="d-flex flex-column">
          <label className="setlist-text-sm figma-text secondary mb-2">Name</label>
          <div className="input-group inline-white" style={inputWrapperStyle}>
            <input
              className="form-control"
              placeholder="Service Fee"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="flex-grow-1" style={{ minWidth: "150px" }}>
        <div className="d-flex flex-column">
          <label className="setlist-text-sm figma-text secondary mb-2">Rate</label>
          <div className="input-group inline-white" style={inputWrapperStyle}>
            <MaskedInput
              className="form-control"
              placeholder="0"
              mask={createNumberMask({
                prefix: "",
                allowDecimal: true,
                decimalLimit: 3,
                allowNegative: false,
                includeThousandsSeparator: false,
              })}
              value={rate}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setRate(e.target.value)}
            />
            <div className="input-group-append">
              <span className="input-group-text px-2">
                <i className="far fa-percentage text-muted"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1" style={{ minWidth: "200px" }}>
        <div className="d-flex flex-column">
          <label className="setlist-text-sm figma-text secondary mb-2">Applies To</label>
          <select
            className="form-control"
            style={{ border: "1px solid var(--sl-border-secondary, #ececec)", borderRadius: "8px" }}
            value={currentCategory}
            onChange={(e) => updateTaxFeeState({ category: e.target.value || null })}
          >
            <option value="">All items</option>
            {isOrphanedCategory && (
              <option value={currentCategory}>{currentCategory} (removed)</option>
            )}
            {categoryOptions.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex-grow-1" style={{ minWidth: "120px" }}>
        <div className="d-flex flex-column">
          <label className="setlist-text-sm figma-text secondary mb-2">Amount</label>
          <div className="setlist-text-md" style={{ paddingTop: "8px" }}>
            {formatCurrency(taxFee.calculated_amount)}
          </div>
        </div>
      </div>
      <div className="d-flex align-items-end" style={{ paddingBottom: "6px" }}>
        <a onClick={deleteTaxFee} className="setlist-text-sm figma-text quinary" style={{ cursor: "pointer" }}>
          <i className="far fa-trash" />
        </a>
      </div>
    </div>
  );
};

export default RentalInvoiceTaxFeeRow;
